/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, Plus, Calendar, Users, Sparkles, X } from 'lucide-react';
import PerceptionImpactForm from '@/components/terra/PerceptionImpactForm';

const STATUS_TONE = {
  PLANNED: 'bg-sky-50 text-sky-800 border-sky-200',
  CLOSED:  'bg-emerald-50 text-emerald-800 border-emerald-300',
};

export default function CentreEventsPage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ event_type: '', title: '', scheduled_for: '', expected_attendees: '' });
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);

  const reloadList = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/terra-growth/events?days=180`);
      setEvents(r.data.events || []);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [tR, leadsR] = await Promise.all([
          axios.get(`${API}/terra-growth/reference/event-templates`),
          axios.get(`${API}/inquiries`).catch(() => ({ data: [] })),
        ]);
        setTemplates(tR.data?.templates || []);
        setLeads(Array.isArray(leadsR.data) ? leadsR.data : leadsR.data?.inquiries || []);
      } catch { /* */ }
      await reloadList();
    })();
  }, [reloadList]);

  const openEvent = useCallback(async (id) => {
    setActiveId(id);
    setActiveEvent(null);
    try {
      const r = await axios.get(`${API}/terra-growth/events/${id}`);
      setActiveEvent(r.data);
    } catch (e) { toast.error('Could not load event.'); }
  }, []);

  const createEvent = async () => {
    if (!form.event_type || !form.title || !form.scheduled_for) {
      toast.error('Type, title and date are required.'); return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.expected_attendees) payload.expected_attendees = parseInt(payload.expected_attendees, 10);
      else delete payload.expected_attendees;
      const r = await axios.post(`${API}/terra-growth/events`, payload);
      toast.success('Event created.');
      setCreating(false);
      setForm({ event_type: '', title: '', scheduled_for: '', expected_attendees: '' });
      await reloadList();
      openEvent(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create.');
    } finally { setBusy(false); }
  };

  const addAttendee = async (lead) => {
    if (!activeEvent) return;
    try {
      await axios.post(`${API}/terra-growth/events/${activeEvent.id}/attendees`,
                        { lead_id: lead.id });
      await openEvent(activeEvent.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed.'); }
  };

  const updateOutcome = async (leadId, payload) => {
    if (!activeEvent) return;
    try {
      await axios.patch(`${API}/terra-growth/events/${activeEvent.id}/attendees/${leadId}`, payload);
      await openEvent(activeEvent.id);
    } catch (e) { toast.error('Outcome save failed.'); }
  };

  const upcoming = useMemo(() => events.filter(e => e.status === 'PLANNED'), [events]);
  const closed = useMemo(() => events.filter(e => e.status === 'CLOSED'), [events]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="centre-events-page" className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Layer 5</p>
            <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Growth Events</h1>
            <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1">
              Offline territory activation. Events measure perception impact, not attendance.
            </p>
          </div>
          {user?.role !== 'representative' && (
            <Button
              onClick={() => setCreating(true)}
              data-testid="event-create-btn"
              className="bg-[#4A5D4E] hover:bg-[#3D4F40] text-white text-xs rounded-full px-4"
            >
              <Plus className="w-3 h-3" /> New event
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-4 space-y-5">
            <Section title="Upcoming" testId="events-upcoming-list">
              {upcoming.length === 0 ? <Empty /> : upcoming.map(e => (
                <EventRow key={e.id} ev={e} active={activeId === e.id} onClick={() => openEvent(e.id)} />
              ))}
            </Section>
            <Section title="Closed · recent" testId="events-closed-list">
              {closed.length === 0 ? <Empty /> : closed.slice(0, 8).map(e => (
                <EventRow key={e.id} ev={e} active={activeId === e.id} onClick={() => openEvent(e.id)} />
              ))}
            </Section>
          </aside>

          <main className="lg:col-span-8 space-y-5">
            {creating ? (
              <div data-testid="event-create-form" className="rounded-2xl border border-[#EBE8E3] bg-white p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-['Outfit'] text-[#2C2A29]">New growth event</h3>
                  <button onClick={() => setCreating(false)}><X className="w-4 h-4 text-[#8A8681]" /></button>
                </div>
                <select
                  value={form.event_type}
                  onChange={(e) => setForm(f => ({ ...f, event_type: e.target.value }))}
                  className="w-full rounded-md border border-[#D8D4CE] px-3 py-2 text-sm font-['Work_Sans']"
                  data-testid="event-type-select"
                >
                  <option value="">— select template —</option>
                  {templates.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                </select>
                <Input data-testid="event-title-input" placeholder="Title" value={form.title}
                       onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
                <Input type="datetime-local" data-testid="event-scheduled-input"
                       value={form.scheduled_for}
                       onChange={(e) => setForm(f => ({ ...f, scheduled_for: e.target.value }))} />
                <Input type="number" placeholder="Expected attendees (optional)"
                       data-testid="event-expected-input"
                       value={form.expected_attendees}
                       onChange={(e) => setForm(f => ({ ...f, expected_attendees: e.target.value }))} />
                <Button data-testid="event-submit-btn" onClick={createEvent} disabled={busy}
                        className="bg-[#4A5D4E] hover:bg-[#3D4F40] text-white text-xs rounded-full px-4">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calendar className="w-3 h-3" />} Create
                </Button>
              </div>
            ) : !activeEvent ? (
              <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
                Select an event from the left or create one.
              </div>
            ) : (
              <EventDetail
                ev={activeEvent} leads={leads}
                onAddAttendee={addAttendee}
                onUpdateOutcome={updateOutcome}
                onClosed={async () => { await reloadList(); openEvent(activeEvent.id); }}
                role={user?.role}
              />
            )}
          </main>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
}

function Section({ title, children, testId }) {
  return (
    <div className="rounded-2xl border border-[#EBE8E3] bg-white p-3" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] px-2 py-1">{title}</p>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}
function Empty() { return <li className="text-xs text-[#8A8681] py-3 text-center">Nothing here.</li>; }

function EventRow({ ev, active, onClick }) {
  return (
    <li>
      <button
        data-testid={`event-row-${ev.id}`}
        onClick={onClick}
        className={`w-full text-left rounded-xl px-3 py-2 transition-all ${active ? 'bg-[#F4F2EE] border border-[#4A5D4E]' : 'border border-transparent hover:bg-[#FBFaf9]'}`}
      >
        <div className="text-sm text-[#2C2A29] truncate">{ev.title}</div>
        <div className="flex items-center gap-1.5 mt-1">
          <span className={`text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full border ${STATUS_TONE[ev.status] || ''}`}>
            {ev.status}
          </span>
          <span className="text-[10px] text-[#8A8681]">{ev.event_type.replace(/_/g, ' ').toLowerCase()}</span>
        </div>
        {ev.perception_impact_score?.composite !== undefined && ev.perception_impact_score?.composite !== null && (
          <div className="text-[10px] text-[#8C6239] mt-0.5">
            Impact · {ev.perception_impact_score.composite}
          </div>
        )}
      </button>
    </li>
  );
}

function EventDetail({ ev, leads, onAddAttendee, onUpdateOutcome, onClosed, role }) {
  const [showLeadPicker, setShowLeadPicker] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = useMemoLeads(leads, search, ev.attendees || []);

  return (
    <>
      <div data-testid="event-detail" className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-xl font-['Outfit'] text-[#2C2A29]">{ev.title}</h2>
            <p className="text-[11px] text-[#8A8681] mt-0.5 uppercase tracking-[0.14em]">
              {ev.event_type.replace(/_/g, ' ')} · {new Date(ev.scheduled_for).toLocaleString()}
            </p>
          </div>
          <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${STATUS_TONE[ev.status] || ''}`}>
            {ev.status}
          </span>
        </div>
        {ev.notes && <p className="mt-3 text-sm text-[#5C5955]">{ev.notes}</p>}
        {ev.perception_impact_score && (
          <div className="mt-3 flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-[#8C6239]" />
            <span className="text-[11px] text-[#5C3D1F]">
              Impact composite · <strong>{ev.perception_impact_score.composite}</strong>
            </span>
          </div>
        )}
      </div>

      <div data-testid="event-attendees" className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-['Outfit'] text-[#2C2A29] flex items-center gap-2">
            <Users className="w-4 h-4" /> Attendees · {ev.attendee_count || 0}
          </h3>
          <Button
            data-testid="event-add-attendee-btn"
            onClick={() => setShowLeadPicker(v => !v)}
            variant="outline"
            className="border-[#D8D4CE] text-[#5C5955] text-xs rounded-full px-3"
          >
            <Plus className="w-3 h-3" /> Link lead
          </Button>
        </div>

        {showLeadPicker && (
          <div data-testid="event-lead-picker" className="mb-3 rounded-xl border border-[#EBE8E3] bg-[#FBFaf9] p-3">
            <Input
              placeholder="Search leads to link…" value={search}
              onChange={(e) => setSearch(e.target.value)} className="h-8 text-xs mb-2"
              data-testid="event-lead-picker-search"
            />
            <ul className="max-h-56 overflow-y-auto space-y-1">
              {filtered.slice(0, 30).map(l => (
                <li key={l.id}>
                  <button
                    data-testid={`event-pick-lead-${l.id}`}
                    onClick={() => { onAddAttendee(l); setShowLeadPicker(false); setSearch(''); }}
                    className="w-full text-left text-xs px-2 py-1.5 rounded-md hover:bg-white border border-transparent hover:border-[#EBE8E3]"
                  >
                    {l.name || '—'} <span className="text-[#8A8681]">· {l.email || l.phone || ''}</span>
                  </button>
                </li>
              ))}
              {filtered.length === 0 && <li className="text-xs text-[#8A8681] text-center py-2">No matches.</li>}
            </ul>
          </div>
        )}

        {(ev.attendees || []).length === 0 ? (
          <p className="text-xs text-[#8A8681]">Link leads who attended or were targeted.</p>
        ) : (
          <ul className="divide-y divide-[#EBE8E3]">
            {ev.attendees.map(a => (
              <li key={a.id} data-testid={`attendee-row-${a.lead_id}`} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm text-[#2C2A29] truncate">{a.lead_name}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {a.perception_state_at_join && (
                      <span className="text-[10px] uppercase tracking-[0.12em] text-[#5C5955] bg-[#F4F2EE] rounded-full px-1.5 py-0.5">
                        from {a.perception_state_at_join}
                      </span>
                    )}
                    {a.perception_state_after && (
                      <span className="text-[10px] uppercase tracking-[0.12em] text-[#4A5D4E] bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                        → {a.perception_state_after}
                      </span>
                    )}
                  </div>
                </div>
                <AttendeeOutcomeControls a={a} onUpdate={(p) => onUpdateOutcome(a.lead_id, p)} disabled={ev.status === 'CLOSED'} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {ev.status !== 'CLOSED' && role !== 'representative' && (
        <PerceptionImpactForm eventId={ev.id} onClosed={onClosed} />
      )}
    </>
  );
}

function useMemoLeads(leads, search, attendees) {
  return useMemo(() => {
    const taken = new Set(attendees.map(a => a.lead_id));
    const q = search.trim().toLowerCase();
    return (leads || []).filter(l => !taken.has(l.id) &&
      (!q || (l.name || '').toLowerCase().includes(q) || (l.email || '').toLowerCase().includes(q)));
  }, [leads, search, attendees]);
}

function AttendeeOutcomeControls({ a, onUpdate, disabled }) {
  const [perception, setPerception] = useState(a.perception_state_after || '');
  const [decode, setDecode] = useState(a.decode_intent_declared || false);
  const [follow, setFollow] = useState(a.follow_up_committed || false);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Input
        placeholder="Perception after"
        value={perception} onChange={(e) => setPerception(e.target.value.toUpperCase())}
        className="h-8 text-[11px] w-44" disabled={disabled}
        data-testid={`attendee-perception-input-${a.lead_id}`}
      />
      <label className="text-[10px] inline-flex items-center gap-1 text-[#5C5955]">
        <input type="checkbox" checked={decode} onChange={(e) => setDecode(e.target.checked)} disabled={disabled} /> DECODE intent
      </label>
      <label className="text-[10px] inline-flex items-center gap-1 text-[#5C5955]">
        <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} disabled={disabled} /> follow-up
      </label>
      <Button
        size="sm" variant="outline" disabled={disabled}
        data-testid={`attendee-save-outcome-${a.lead_id}`}
        onClick={() => onUpdate({
          attended: true,
          perception_state_after: perception || null,
          decode_intent_declared: decode,
          follow_up_committed: follow,
        })}
        className="border-[#D8D4CE] text-[#5C5955] text-[10px] rounded-full px-3 h-7"
      >
        Save
      </Button>
    </div>
  );
}
